import React, { useEffect, useState } from "react";
import * as documentService from "../../../models/services/documentService";
import { ISessionState } from "../../../models/session/sessionState";
import { ListDetailsPanel } from "source/components/framework/List/ListDetailsPanel";
import { AppUtils } from "source/framework/appUtils";
import { Translations } from "ui/Scripts/source/models/translations";
import { Alert } from "@mui/material";
import { IDocumentVersionPreview } from "source/models/document/documentVersionPreview";
import { IDocumentContentPreview } from "source/models/document/documentContentPreview";
import { FileType } from "ui/Scripts/source/models/enums";
import { DocumentListDetailsPanelDocumentContent } from "./DocumentListDetailsPanelDocumentContent";
import { IDocumentFilePreview } from "source/models/document/documentFilePreview";
import { DocumentListDetailsPanelFileContent } from "./DocumentListDetailsPanelFileContent";
import { DownloadToolButton } from "source/components/framework/Button/DownloadToolButton";
import { ChangeToolButton } from "source/components/framework/Button/ChangeToolButton";
import { Base } from "ui/Scripts/source/framework/base";

interface IDocumentListDetailsPanelProps {
    documentsLoading: boolean;
    showPublishedVersion: boolean;
    sessionState: ISessionState;
    companyId: string;
    documentId: string;
    documentVersionId: string;
    publishedId: string;
    documentFileType: number;
    dataId: string;
    editDialogVisible: boolean;
    onDownloadDocument: (id: string) => Promise<void>;
    onChangeShowPublishedVersion: (showPublishedVersion: boolean) => void;
    onClose: () => void;
}

export const DocumentListDetailsPanel = (props: IDocumentListDetailsPanelProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [documentVersionPreview, setDocumentVersionPreview] = useState<IDocumentVersionPreview>(null);
    const [documentContentPreview, setDocumentContentPreview] = useState<IDocumentContentPreview>(null);
    const [documentFilePreview, setDocumentFilePreview] = useState<IDocumentFilePreview>(null);

    const loadDocumentVersion = async () => {
        setIsLoading(true);
        const result = await AppUtils.callService(() => documentService.getDocumentVersionPreview(props.companyId, props.documentVersionId), false, true, () => setIsLoading(false));
        if (!result) return;
        setDocumentFilePreview(null);
        setDocumentContentPreview(null);
        setDocumentVersionPreview(result);
    };

    const loadDocumentContent = async () => {
        setIsLoading(true);
        const result = await AppUtils.callService(() => documentService.getDocumentContentPreview(props.companyId, props.documentId), false, true, () => setIsLoading(false));
        if (!result) return;
        setDocumentFilePreview(null);
        setDocumentVersionPreview(null);
        setDocumentContentPreview(result);
    };

    const loadFileContent = async () => {
        setIsLoading(true);
        const result = await AppUtils.callService(() => documentService.getFileContentPreview(props.companyId, props.documentId, props.documentFileType), false, true, () => setIsLoading(false));
        if (!result) return;
        setDocumentVersionPreview(null);
        setDocumentContentPreview(null);
        setDocumentFilePreview(result);
    };

    const loadDetails = async () => {
        if (!props.documentId || Base.isNullOrUndefined(props.documentFileType)) {
            setDocumentFilePreview(null);
            setDocumentContentPreview(null);
            setDocumentVersionPreview(null);
            return;
        }
        if (props.documentFileType === FileType.Document) {
            if (props.documentVersionId && props.showPublishedVersion) {
                loadDocumentVersion();
            } else {
                loadDocumentContent();
            }
        } else {
            if (props.documentVersionId) {
                loadDocumentVersion();
            } else {
                loadFileContent();
            }
        }
    };

    useEffect(() => {
        if (!props.companyId || props.documentsLoading) return;
        if (props.editDialogVisible) return;
        loadDetails();
    }, [props.companyId, props.documentId, props.documentVersionId, props.dataId, props.showPublishedVersion, props.editDialogVisible]);

    const hasContent = !!documentVersionPreview || !!documentContentPreview || !!documentFilePreview;
    const downloadDirectoryItemId = props.documentFileType === FileType.Document ? props.publishedId : props.documentId;
    const canChangeVisibleVersion = props.documentFileType === FileType.Document && props.documentVersionId;

    const handlePublishedDocumentDownload = async (e) => {
        e.stopPropagation();
        await props.onDownloadDocument(downloadDirectoryItemId);
    };

    const handleChangeDocumentVersion = async (e) => {
        e.stopPropagation();
        props.onChangeShowPublishedVersion(!props.showPublishedVersion);
    };

    const subTitle = props.documentId
        ? (documentVersionPreview
            ? "(" + Translations.PublishVersion + ")"
            : (documentContentPreview
                ? "(" + Translations.DraftVersion + ")"
                : null))
        : null;

    const buttons = <>
        {canChangeVisibleVersion &&
            <ChangeToolButton onClick={handleChangeDocumentVersion} tooltip={props.showPublishedVersion ? Translations.ShowDraftVersion : Translations.ShowPublishVersion}/>
        }
        {downloadDirectoryItemId &&
            <DownloadToolButton onClick={handlePublishedDocumentDownload}/>
        }
    </>;

    return (
        <ListDetailsPanel
            onClose={props.onClose}
            flexGrow={3}
            maxWidth="calc((100vw - 192px - 350px - 48px) / 2)"
            isLoading={isLoading}
        >
            {(!props.documentId || (isLoading && !hasContent)) &&
                <Alert severity="info">{Translations.SelectDocumentInListToSeeThePreview}</Alert>
            }
            {!!props.documentId &&
                <>
                    {!!documentVersionPreview &&
                        <DocumentListDetailsPanelDocumentContent
                            companyId={props.companyId}
                            directoryItemId={documentVersionPreview.directoryItemId}
                            comment={null}
                            content={documentVersionPreview.content}
                            createUser={documentVersionPreview.createUser}
                            modifyUser={documentVersionPreview.modifyUser}
                            errorMessage={documentVersionPreview.errorMessage}
                            subTitle={subTitle}
                            buttons={buttons}
                        />
                    }
                    {!!documentContentPreview &&
                        <DocumentListDetailsPanelDocumentContent
                            companyId={props.companyId}
                            directoryItemId={props.publishedId}
                            comment={documentContentPreview.comment}
                            content={documentContentPreview.content}
                            createUser={documentContentPreview.createUser}
                            modifyUser={documentContentPreview.modifyUser}
                            errorMessage={documentContentPreview.errorMessage}
                            subTitle={subTitle}
                            buttons={buttons}
                        />
                    }
                    {!!documentFilePreview &&
                        <DocumentListDetailsPanelFileContent
                            companyId={props.companyId}
                            directoryItemId={props.publishedId}
                            documentFilePreview={documentFilePreview}
                        />
                    }
                </>
            }
        </ListDetailsPanel>
    );
};
